/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { ClusterStatusNotification } from '../components/notifications/ClusterStatusNotification';
import { DatasetImportNotification } from '../components/notifications/DatasetImportNotification';
import { LogicalBackupNotification } from '../components/notifications/LogicalBackupNotification';
import { OrganizationInvitationNotification } from '../components/notifications/OrganizationInvitationNotification';
import { ProjectMembershipNotification } from '../components/notifications/ProjectMembershipNotification';
import { RemovedFromOrganizationNotification } from '../components/notifications/RemovedFromOrganizationNotification';
import { SeeInvitationsNotification } from '../components/notifications/SeeInvitationsNotification';
import { TextNotification } from '../components/notifications/TextNotification';
import { removeNotification } from '../store/notifications/actions';
import { INotification } from '../store/notifications/types';
import { AppEvents, AppServerEvents, AppStandaloneEvents } from '../types/common/AppEvents';
import { NotificationItemFrameOptions } from '../types/notification/NotificationItemFrameOptions';

type FnType = (
  params: { item: INotification<AppEvents> } & NotificationItemFrameOptions,
) => React.ReactElement<any> | undefined;

export const useRenderNotificationItem = (): FnType => {
  const dispatch = useDispatch();

  return useCallback<FnType>(
    params => {
      const { item, ...notificationItemOptions } = params;
      let element: React.ReactElement<any> | undefined;
      switch (item.type) {
        case AppServerEvents.ClusterCreated:
        case AppServerEvents.ClusterUpdated:
          element = (
            <ClusterStatusNotification
              key={item.id}
              notification={item as INotification<AppServerEvents.ClusterCreated>}
              {...notificationItemOptions}
            />
          );
          break;
        case AppServerEvents.ImportStarted:
        case AppServerEvents.ImportSuccessful:
        case AppServerEvents.ImportAborted:
        case AppServerEvents.ImportFailedInsufficientSpace:
        case AppServerEvents.ImportFailedSchemaExists:
        case AppServerEvents.ImportFailedDatabaseNotFound:
        case AppServerEvents.ImportFailedInvalidCredentials:
        case AppServerEvents.ImportFailed:
          element = (
            <DatasetImportNotification
              key={item.id}
              notification={item as INotification<AppServerEvents.ImportStarted>}
              {...notificationItemOptions}
            />
          );
          break;
        case AppServerEvents.OrganizationInvitation:
          element = (
            <OrganizationInvitationNotification
              key={item.id}
              notification={item as INotification<AppServerEvents.OrganizationInvitation>}
              {...notificationItemOptions}
            />
          );
          break;
        case AppServerEvents.RemovedFromOrganization:
          element = (
            <RemovedFromOrganizationNotification
              key={item.id}
              notification={item as INotification<AppServerEvents.RemovedFromOrganization>}
              {...notificationItemOptions}
            />
          );
          break;
        case AppServerEvents.AddedToProject:
        case AppServerEvents.RemovedFromProject:
          element = (
            <ProjectMembershipNotification
              key={item.id}
              notification={item as INotification<AppServerEvents.AddedToProject>}
              {...notificationItemOptions}
            />
          );
          break;
        case AppStandaloneEvents.SeeInvitations:
          element = (
            <SeeInvitationsNotification
              key={item.id}
              notification={item as INotification<AppStandaloneEvents.SeeInvitations>}
              {...notificationItemOptions}
            />
          );
          break;
        case AppStandaloneEvents.TextToastr: {
          const notification = item as INotification<AppStandaloneEvents.TextToastr>;
          element = (
            <TextNotification
              key={item.id}
              notification={notification}
              {...notificationItemOptions}
              primaryText={notification.event.primaryText}
              secondaryText={notification.event.secondaryText}
            />
          );
          break;
        }
        case AppServerEvents.LogicalBackupCompleted:
        case AppServerEvents.LogicalBackupFailed:
          element = (
            <LogicalBackupNotification
              key={item.id}
              notification={item as INotification<AppServerEvents.LogicalBackupCompleted>}
              {...notificationItemOptions}
            />
          );
          break;
        default:
          dispatch(removeNotification({ id: item.id }));
          element = undefined;
          break;
      }

      return element;
    },
    [dispatch],
  );
};
